import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import LoadingPage from "../../public/LoadingPage";
import { useGetProcessUIData } from "../../../hooks/entity";
import { useLmcUserView } from "../../../contexts/AuthContext";
import EventCardDisplay from "../../atoms/EventCardDisplay";
import { ProcessData } from "../../../types/BusinessCardTypes";
import { EventProcessData } from "../../../types/EventPaymentTypes";
import LoyaltyCardContainer from "../../atoms/LoyaltyCardContainer";
import BusinessCardFooter from "../../atoms/BusinessCardFooter";
import { HiCheckCircle } from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { generateEventHelmet } from "../../../utils/helmetUtils";
import LanguageSelector from "../../atoms/LanguageSelector";

interface LocationState {
  from?: string;
  processData?: ProcessData;
  cacheTimestamp?: number;
}

const CACHE_TTL = 2 * 60 * 1000;

const EventApp = () => {
  const { t } = useTranslation("clientView");
  const { processId } = useParams<{ processId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useLmcUserView();

  const navigationState = location.state as LocationState;
  const hasNavigationData = Boolean(navigationState?.processData);

  const isCacheValid = useMemo(() => {
    if (!hasNavigationData || !navigationState?.cacheTimestamp) {
      return false;
    }
    const now = Date.now();
    const cacheAge = now - navigationState.cacheTimestamp;
    return cacheAge < CACHE_TTL;
  }, [hasNavigationData, navigationState?.cacheTimestamp]);

  const cachedProcessData = isCacheValid
    ? navigationState?.processData || null
    : null;

  const {
    data: processData,
    isLoading,
    isError,
  } = useGetProcessUIData(processId as string, {
    enabled: !isCacheValid && !!processId,
    onSuccess: (data) => {
      dispatch({
        type: "ENTITY",
        payload: data,
      });
    },
  });

  const finalProcessData = cachedProcessData || processData;

  useEffect(() => {
    if (finalProcessData && finalProcessData.process_type !== "events_config") {
      toast.error(t("event.errors.invalidProcess"));
      navigate("/");
    }
  }, [finalProcessData, navigate, t]);

  if (isLoading && !cachedProcessData) {
    return <LoadingPage />;
  }

  if (isError) {
    const gradientClass = "bg-gradient-to-br from-blue-400 to-blue-600";

    const errorContent = (
      <div className="w-full h-full" translate="no">
        <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
          <div className="row-start-1 row-end-2 min-h-0">
            <div
              className="h-full overflow-y-auto overscroll-contain touch-pan-y"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 backdrop-blur m-3 sm:m-4">
                <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                  <div
                    className={`absolute inset-0 opacity-95 ${gradientClass}`}
                  />
                  <div className="absolute top-3 end-3 z-[9999]">
                    <LanguageSelector variant="compact" />
                  </div>
                  <div className="relative z-10 flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 border-white/60 shadow-lg bg-white p-2">
                      <img
                        src="https://cdn.1cdn.app/application/UMD/2405110558455_logo.png"
                        alt="Super User Manager"
                        className="object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-2xl font-bold text-white leading-tight">
                        {t("event.name")}
                      </h2>
                      <p className="text-xs text-white/90 mt-0.5 font-medium">
                        {t("event.errors.poweredBy")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 backdrop-blur m-3 sm:m-4">
                <div className="relative px-5 pt-8 pb-8 sm:px-6 sm:pt-10 sm:pb-10 flex flex-col">
                  <div
                    className={`absolute inset-0 opacity-95 ${gradientClass}`}
                  />
                  <div className="relative z-10 flex flex-col gap-5 items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/40 shadow-lg">
                      <HiTicket size={40} className="text-white" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {t("event.errors.notFound")}
                      </h3>
                      <p className="text-white/90 text-sm leading-relaxed max-w-xs mx-auto">
                        {t("event.errors.notFoundMessage")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
                <div className="relative px-5 pt-6 pb-6 sm:px-6 sm:pt-7 sm:pb-7 flex flex-col">
                  <div
                    className={`absolute inset-0 opacity-95 ${gradientClass}`}
                  />
                  <div className="relative z-10 flex flex-col gap-5">
                    <div className="text-center">
                      <h4 className="text-xl font-bold text-white mb-2">
                        {t("event.promo.title")}
                      </h4>
                      <p className="text-sm text-white/90 mb-5 font-medium leading-relaxed">
                        {t("event.promo.subtitleShort")}
                      </p>
                      <ul className="text-start text-sm text-white/90 space-y-3">
                        <li className="flex items-start gap-3">
                          <HiCheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                          <span className="font-medium">
                            {t("event.promo.features.unlimited")}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <HiCheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                          <span className="font-medium">
                            {t("event.promo.features.tickets")}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <HiCheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                          <span className="font-medium">
                            {t("event.promo.features.payment")}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row-start-3 row-end-4">
            <BusinessCardFooter showFooter={true} theme="default" />
          </div>
        </div>
      </div>
    );

    return (
      <LoyaltyCardContainer
        frontContent={errorContent}
        backContent={errorContent}
        isPreview={false}
        isFlipping={false}
        showEmailLogin={false}
        className="max-w-[360px] w-full"
        fullHeight={true}
      />
    );
  }

  if (!finalProcessData) {
    return <LoadingPage />;
  }

  const eventData = finalProcessData as EventProcessData;

  return (
    <>
      {generateEventHelmet({
        processData: finalProcessData,
        processId,
      })}
      <EventCardDisplay processId={processId as string} eventData={eventData} />
    </>
  );
};

export default EventApp;
